import React from 'react'
import { Crown, Lock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useProaStore } from '@/stores/proa-store'
import { useListScenarios } from '../hooks/useProaData'

interface ScenarioSelectorProps {
  /** Callback when "Create New" is clicked */
  onCreateNew?: () => void
  /** Custom class name */
  className?: string
  /** Show create button */
  showCreateButton?: boolean
}

/**
 * Scenario Selector Component
 *
 * Dropdown selector for PROA scenarios with:
 * - Year-filtered scenario list
 * - Official/locked badge indicators
 * - Create new button (optional)
 */
export function ScenarioSelector({
  onCreateNew,
  className,
  showCreateButton = true,
}: ScenarioSelectorProps) {
  const {
    selectedScenarioId,
    selectedYear,
    setSelectedScenario,
    setSelectedYear,
  } = useProaStore()

  const { data: scenarios, isLoading } = useListScenarios(selectedYear)

  // Find selected scenario from list
  const selectedScenario = scenarios?.find(s => s.scenarioId === selectedScenarioId)

  const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const scenarioId = e.target.value
    if (scenarioId === '__create__') {
      onCreateNew?.()
      // Reset to previous value
      e.target.value = selectedScenarioId || ''
      return
    }
    if (!scenarioId) {
      setSelectedScenario(null)
      return
    }
    const scenario = scenarios?.find(s => s.scenarioId === scenarioId)
    if (scenario) {
      setSelectedScenario(scenario)
    }
  }

  const yearOptions = [2023, 2024, 2025, 2026]

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Year Selector */}
      <select
        value={selectedYear}
        onChange={(e) => setSelectedYear(Number(e.target.value))}
        className="h-10 rounded-md border border-[#2A2D34] bg-[#1E2024] px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#D2FA52] focus:ring-offset-2 focus:ring-offset-[#1E2024]"
      >
        {yearOptions.map(year => (
          <option key={year} value={year}>{year}</option>
        ))}
      </select>

      {/* Scenario Selector */}
      <div className="relative">
        <select
          value={selectedScenarioId || ''}
          onChange={handleSelect}
          disabled={isLoading}
          className="h-10 w-[280px] rounded-md border border-[#2A2D34] bg-[#1E2024] px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#D2FA52] focus:ring-offset-2 focus:ring-offset-[#1E2024] disabled:opacity-50 appearance-none cursor-pointer"
        >
          <option value="">
            {isLoading ? 'Carregando...' : 'Selecione um cenario...'}
          </option>
          {scenarios?.map((scenario) => (
            <option key={scenario.scenarioId} value={scenario.scenarioId}>
              {scenario.isOfficial ? '' : ''}
              {scenario.isLocked ? '' : ''}
              {scenario.name}
            </option>
          ))}
          {showCreateButton && onCreateNew && (
            <option value="__create__">+ Criar novo cenario</option>
          )}
        </select>

        {/* Custom dropdown icon */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center gap-1">
          {selectedScenario?.isOfficial && (
            <Crown className="h-4 w-4 text-[#D2FA52]" />
          )}
          {selectedScenario?.isLocked && (
            <Lock className="h-4 w-4 text-[#838998]" />
          )}
          <svg className="h-4 w-4 text-[#838998]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
    </div>
  )
}

export default ScenarioSelector
